import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  IconButton,
  Paper,
  Portal,
  Typography,
} from "@material-ui/core";
import { makeStyles, ThemeProvider } from "@material-ui/core/styles";
import { AddShoppingCart, AspectRatio, Close, DragIndicator, Refresh, Remove, Storefront } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  CustomerServiceDialog,
  SendPromptDialog,
  ServiceRowActions,
} from "../../pages/Negocio/CustomerServiceDialog";
import { AddCartItemDialog, CartConfigDialog, PurchasedItemDialog } from "../../pages/Negocio/CartDialog";
import { tNegocio } from "../../pages/Negocio/shared";
import { AccountPeek } from "../../pages/Negocio/AccountPeek";
import { daysLeft, subSlotText } from "../../pages/Negocio/helpers";
import { useNegocioBridge, useNegocioLive } from "../../pages/Negocio/useNegocioSync";

const placePanel = (minimized) => {
  const width = minimized ? 280 : Math.min(440, window.innerWidth - 16);
  return {
    x: Math.max(8, window.innerWidth - width - 12),
    y: Math.max(64, Math.min(96, window.innerHeight - 220)),
  };
};

const useStyles = makeStyles((theme) => ({
  panel: {
    position: "fixed",
    zIndex: 190000,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    background: theme.palette.background.paper,
    boxShadow: "0 16px 40px rgba(15,23,42,.28)",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 6px 6px 8px",
    cursor: "grab",
    touchAction: "none",
    userSelect: "none",
    "& > div": { flex: 1, minWidth: 0 },
  },
  body: { overflow: "auto", padding: 12, minHeight: 0 },
  service: {
    padding: 12,
    marginBottom: 10,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflowWrap: "anywhere",
    cursor: "pointer",
    "& .actionCluster": {
      display: "flex !important",
      flexWrap: "wrap",
      whiteSpace: "normal !important",
      gap: 4,
      marginTop: 8,
    },
    "& .actionCluster > .MuiIconButton-root": { width: 40, height: 40 },
  },
  serviceHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  remaining: { flexShrink: 0, fontSize: 12, fontWeight: 600, textAlign: "right" },
}));
const ticketDialogTheme = (outer) => ({
  ...outer,
  zIndex: { ...outer.zIndex, modal: 210000, snackbar: 210100, tooltip: 210200 },
});

export const remainingTime = (sub) => {
  if (sub.status === "cancelled") return "Cancelado";
  const left = daysLeft(sub.endDate);
  if (left == null || !Number.isFinite(left)) return "Sin vencimiento";
  if (left < 0) return `Vencido hace ${Math.abs(left)} ${left === -1 ? "día" : "días"}`;
  if (left === 0) return "Vence hoy";
  return `${left} ${left === 1 ? "día restante" : "días restantes"}`;
};

export default function NegocioCustomerMenu({ ticket, menuOpen, open, onClose, onAvailable }) {
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const contactId = ticket.contact?.id || ticket.contactId;
  const allowed = !!(user?.super || user?.profile === "admin");
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [editor, setEditor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [sendPrompt, setSendPrompt] = useState(null);
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [pickedId, setPickedId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const drag = useRef(null);
  const generation = useRef(0);
  const batchSinceRef = useRef(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, [open]);
  useNegocioBridge();
  const load = useCallback(
    async (reportError = false) => {
      if (!allowed || !contactId) return;
      const current = ++generation.current;
      try {
        const { data } = await api.get(`/negocio/customers/${contactId}`);
        if (generation.current === current) setCustomer({ ...data, contactId });
      } catch (error) {
        if (generation.current !== current) return;
        setCustomer(null);
        if (reportError && ![403, 404].includes(error.response?.status)) toastError(error);
      }
    },
    [allowed, contactId]
  );
  useEffect(() => {
    if (!allowed || !open) return undefined;
    let active = true;
    api.get("/negocio/payment-methods").then(({ data }) => {
      if (active) setPayments(Array.isArray(data?.records) ? data.records : []);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [allowed, open]);
  useEffect(() => {
    if (open && !pos) setPos(placePanel(false));
  }, [open, pos]);
  useEffect(() => {
    generation.current += 1;
    setCustomer(null);
    onClose();
    setEditor(null);
    setAddOpen(false);
    setConfigOpen(false);
    setSendPrompt(null);
    batchSinceRef.current = null;
    setPeekAccountId(null);
    setPickedId(null);
    setMinimized(false);
    return () => {
      generation.current += 1;
    };
    // onClose is a parent state setter wrapper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, ticket.id, allowed]);
  useEffect(() => {
    if (menuOpen) load(true);
  }, [menuOpen, load]);
  useNegocioLive(["customers", "subscriptions", "sales", "accounts"], () =>
    open || menuOpen ? load() : undefined
  );
  const notify = async ({ kind, channel, subscriptionId }) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/negocio/notify", { notify: true, kind, channel, contactId, subscriptionId });
      toast.success(tNegocio("pages.customers.sentOk"));
      await load();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    onAvailable(customer);
  }, [customer, onAvailable]);
  const moveBy = (event) => {
    if (!drag.current || !panelRef.current) return;
    const width = panelRef.current.offsetWidth;
    const height = panelRef.current.offsetHeight;
    setPos({
      x: Math.min(Math.max(8, event.clientX - drag.current.dx), window.innerWidth - width - 8),
      y: Math.min(Math.max(8, event.clientY - drag.current.dy), window.innerHeight - height - 8),
    });
  };
  if (!allowed || !customer || !open || !pos) return null;
  const services = customer.subscriptions || [];
  // Se toma de la lista actual para reflejar cambios hechos desde la ficha.
  const pickedService = pickedId ? services.find((s) => Number(s.id) === Number(pickedId)) || null : null;
  const openSendLast = (target, subscriptionIds = []) => {
    if (!target) return;
    if (
      batchSinceRef.current == null ||
      Number(batchSinceRef.current.contactId) !== Number(target.contactId)
    ) {
      batchSinceRef.current = { contactId: target.contactId, ids: [] };
    }
    const batch = batchSinceRef.current;
    batch.ids = [...new Set([...batch.ids, ...subscriptionIds])];
    setSendPrompt({
      kind: "last",
      customer: target,
      cart: true,
      subscriptionIds: batch.ids,
    });
  };
  const closeSendPrompt = () => {
    setSendPrompt(null);
    batchSinceRef.current = null;
  };
  const startAdd = () => {
    if (customer.cartSaleType) setAddOpen(true);
    else setConfigOpen(true);
  };
  return (
    <ThemeProvider theme={ticketDialogTheme}>
      <Portal>
      <Paper
        ref={panelRef}
        className={classes.panel}
        role="dialog"
        aria-labelledby="ticket-negocio-title"
        elevation={8}
        style={{
          left: pos.x,
          top: pos.y,
          width: minimized ? 280 : "min(440px, calc(100vw - 16px))",
          maxHeight: minimized ? 64 : "min(70dvh, calc(100dvh - 24px))",
        }}
      >
        <div
          className={classes.title}
          onPointerDown={(event) => {
            if (event.target.closest("button")) return;
            drag.current = { dx: event.clientX - pos.x, dy: event.clientY - pos.y };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={moveBy}
          onPointerUp={() => { drag.current = null; }}
        >
          <DragIndicator fontSize="small" color="disabled" />
          <Storefront color="primary" fontSize="small" />
          <div>
            <Typography variant="subtitle2" component="h2" id="ticket-negocio-title">
              Cliente de Negocio
            </Typography>
            <Typography variant="caption" noWrap component="p">
              {minimized
                ? `${services.length} servicios`
                : (customer.name || ticket.contact?.name)}
            </Typography>
          </div>
          {!minimized && (
            <IconButton size="small" aria-label="Actualizar servicios" onClick={() => load(true)}>
              <Refresh fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" aria-label={minimized ? "Restaurar cliente de Negocio" : "Minimizar cliente de Negocio"} onClick={() => setMinimized((value) => !value)}>
            {minimized ? <AspectRatio fontSize="small" /> : <Remove fontSize="small" />}
          </IconButton>
          <IconButton size="small" aria-label="Cerrar cliente de Negocio" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </div>
        {!minimized && (
          <div className={classes.body}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
              {customer.number || ticket.contact?.number}
              {customer.customerType ? ` · ${customer.customerType}` : ""} · {services.length}{" "}
              servicios
            </Typography>
            <Button
              fullWidth
              size="small"
              color="primary"
              variant="contained"
              startIcon={<AddShoppingCart />}
              onClick={startAdd}
              aria-label={tNegocio("pages.services.cart.addItem")}
              style={{ marginBottom: 12 }}
            >
              {tNegocio("pages.services.cart.addItem")}
            </Button>
            {!services.length && (
              <Typography variant="body2">
                Este cliente todavía no tiene servicios registrados.
              </Typography>
            )}
            {services.map((sub) => (
              <div
                key={sub.id}
                className={classes.service}
                role="button"
                tabIndex={0}
                onClick={() => setPickedId(sub.id)}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && event.key === "Enter") setPickedId(sub.id);
                }}
              >
                <div className={classes.serviceHeader}>
                  <Typography variant="subtitle2">
                    {sub.product?.name || "Servicio"}
                    {sub.product?.planLabel ? ` · ${sub.product.planLabel}` : ""}
                  </Typography>
                  <Typography
                    component="span"
                    className={classes.remaining}
                    color={
                      daysLeft(sub.endDate) != null && daysLeft(sub.endDate) <= 3
                        ? "error"
                        : "textSecondary"
                    }
                  >
                    {remainingTime(sub)}
                  </Typography>
                </div>
                <Typography variant="body2" color="textSecondary">
                  {{
                    active: "Activo",
                    expired: "Vencido",
                    pending: "Pendiente",
                    cancelled: "Cancelado",
                  }[sub.status] || sub.status}{" "}
                  · {sub.endDate ? `Vence: ${String(sub.endDate).slice(0, 10)}` : "Sin vencimiento"}
                </Typography>
                {sub.account?.email && (
                  <Typography variant="body2">
                    {sub.account.email}
                    {subSlotText(sub) ? ` · ${subSlotText(sub)}` : ""}
                  </Typography>
                )}
                <fieldset disabled={busy} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                  <ServiceRowActions
                    sub={sub}
                    customer={customer}
                    payments={payments}
                    onEdit={() => setEditor({ sub, mode: "edit" })}
                    onRenew={() => setEditor({ sub, mode: "renew" })}
                    onNotify={notify}
                    onDeleted={() => load(true)}
                    onChanged={() => load(true)}
                  />
                </fieldset>
              </div>
            ))}
          </div>
        )}
      </Paper>
      </Portal>
      <PurchasedItemDialog
        open={!!pickedService}
        item={pickedService}
        customer={customer}
        payments={payments}
        onClose={() => setPickedId(null)}
        onPeekAccount={(accountId) => setPeekAccountId(accountId)}
        onChanged={() => load(true)}
        onNotify={notify}
        onRenewed={(payload) => setSendPrompt(payload)}
      />
      <AccountPeek
        open={!!peekAccountId}
        accountId={peekAccountId}
        onClose={() => setPeekAccountId(null)}
        onChanged={() => load(true)}
      />
      <CustomerServiceDialog
        open={!!editor}
        mode={editor?.mode}
        sub={editor?.sub}
        customer={customer}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          setEditor(null);
          await load(true);
        }}
      />
      <CartConfigDialog
        open={configOpen}
        customers={[customer]}
        payments={payments}
        preset={customer}
        lockCustomer
        onClose={() => setConfigOpen(false)}
        onSaved={async () => {
          setConfigOpen(false);
          try {
            const { data } = await api.get(`/negocio/customers/${contactId}`);
            setCustomer({ ...data, contactId });
            setAddOpen(true);
          } catch (error) {
            toastError(error);
          }
        }}
      />
      <AddCartItemDialog
        open={addOpen}
        customer={customer}
        payments={payments}
        cartHasItems={services.length > 0 || batchSinceRef.current != null}
        onClose={() => setAddOpen(false)}
        onSaved={(result) => {
          setAddOpen(false);
          openSendLast(customer, result?.subscriptionIds || []);
          load(true);
        }}
      />
      <SendPromptDialog
        prompt={sendPrompt}
        onClose={closeSendPrompt}
        onAddAnother={() => {
          setSendPrompt(null);
          setAddOpen(true);
        }}
        onSend={async (prompt, channel) => {
          try {
            const single = !prompt.cart && prompt.kind === "renew" && prompt.subscriptionId;
            await api.post("/negocio/notify", single ? {
              notify: true,
              kind: "renew",
              channel,
              contactId,
              subscriptionId: prompt.subscriptionId,
            } : {
              notify: true,
              cart: true,
              kind: prompt.kind || "last",
              channel,
              contactId,
              ...(prompt.subscriptionIds?.length ? { subscriptionIds: prompt.subscriptionIds } : {}),
            });
            toast.success(tNegocio("pages.customers.sentOk"));
            closeSendPrompt();
            await load(true);
          } catch (error) {
            toastError(error);
          }
        }}
      />
    </ThemeProvider>
  );
}

NegocioCustomerMenu.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contactId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contact: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      number: PropTypes.string,
    }),
  }).isRequired,
  menuOpen: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAvailable: PropTypes.func.isRequired,
};
